import React, { ReactElement, useEffect, useId, useRef, useState } from 'react'
import Header from 'src/components/Header/Header'

import CardActionsMenu from './CardActionsMenu/CardActionsMenu'
import CardArt from './CardArt/CardArt'
import * as style from './CardDetailHero.style'
import { PhysicalCardManagementStatus } from './cardDetailHeroDemoData'
import CardDisplayActions from './CardDisplayActions/CardDisplayActions'
import CardHeroCompact from './CardHeroCompact/CardHeroCompact'
import CardRevealPanel from './CardRevealPanel/CardRevealPanel'
import CardSummary from './CardSummary/CardSummary'
import { useCardDetailHeroData } from './useCardDetailHeroData'
import { useCardRevealDisclosure } from './useCardRevealDisclosure'
import { useCompactHeroSwap } from './useCompactHeroSwap'

type CardDetailHeroProps = {
  /**
   * The card this hero describes. The hero is a feature, so it never resolves
   * the id itself — the page owns routing and passes it down (root CLAUDE.md
   * layering, eslint-enforced). The whole credit-card model is deliberately NOT
   * passed: it is a legacy class-backed model, and class instances must not
   * cross into query-cache-backed code.
   */
  creditCardId: string | undefined
  /**
   * Whether this user holds `canUpdateCreditCard` for this card. The page
   * already resolves the card's permissions for its other actions, so the hero
   * reads the same answer rather than issuing its own permissions query.
   */
  canUpdateCard: boolean
  /**
   * Runtime banner offset, read from redux by the page (the feature stays
   * redux-free). Drives both the swap threshold and the pinned compact bar's
   * fixed `top`, so a banner opening shifts them together.
   */
  fixedElementPushdown: number
  /**
   * Whether the sidenav is locked open, also read from redux by the page. Lets
   * the pinned compact bar inset from the left by the sidenav's reserved width
   * so its leading content is not hidden behind the fixed sidenav.
   */
  sideNavLockedOpen: boolean
}

const CardDetailHero: React.FC<CardDetailHeroProps> = ({
  creditCardId,
  canUpdateCard,
  fixedElementPushdown,
  sideNavLockedOpen,
}): ReactElement => {
  const { card, billing, credentials } = useCardDetailHeroData(creditCardId)

  // The full hero's reveal-panel id. Per-instance (`useId`, review finding #10)
  // rather than a module constant: the compact bar mounts its own panel with its
  // own id, so neither Show details trigger's aria-controls can resolve to the
  // wrong panel while both heroes are mounted.
  const revealPanelId = useId()

  // The shell owns card status so the summary pill, the card-art overlay, and
  // the freeze button label always agree — the design spec's "one owner" rule.
  // Demo-stub: Freeze toggles active⇄frozen locally; issuer-imposed LOCKED is
  // left unchanged (its action is "Request unlock", not a voluntary freeze).
  //
  // The server status is now real, so the local toggle is held as an override
  // rather than as seeded state: seeding `useState` from a value that starts
  // undefined and resolves later would pin the pill to the loading value
  // forever.
  const serverStatus = card?.status
  const [freezeOverride, setFreezeOverride] = useState<
    PhysicalCardManagementStatus | undefined
  >(undefined)
  const [observedServerStatus, setObservedServerStatus] = useState(serverStatus)
  if (serverStatus !== observedServerStatus) {
    // Adjusting state during render (React's documented pattern for deriving
    // from changed inputs) — deliberately not a `useEffect`: a re-fetched card
    // status must drop the local demo freeze in the same commit that renders it,
    // and this path touches no network or cache work.
    setObservedServerStatus(serverStatus)
    setFreezeOverride(undefined)
  }
  const status = freezeOverride ?? serverStatus

  const toggleFreeze = (): void => {
    if (status === 'ACTIVE') setFreezeOverride('FROZEN')
    else if (status === 'FROZEN') setFreezeOverride('ACTIVE')
  }

  // The shell also owns the reveal disclosure (same "one owner" rule); both the
  // full hero and the compact bar share it, anchored to whichever opened it.
  const reveal = useCardRevealDisclosure()
  const { close: closeReveal } = reveal

  // The full→compact swap: observe the full hero's bottom edge and flip once it
  // passes under the global header. Both heroes stay mounted; the off-screen one
  // is inert.
  const fullHeroRef = useRef<HTMLElement>(null)
  const compactOpen = useCompactHeroSwap(fullHeroRef, fixedElementPushdown)

  // Close the reveal across any swap so a panel is never left anchored to a
  // now-inert hero (plan §3 rework #2 / §5). `closeReveal` is stable.
  useEffect(() => {
    closeReveal()
  }, [compactOpen, closeReveal])

  return (
    // Reuse the legacy Header layout so the hero occupies the exact page band
    // (white background + standard responsive header padding) the AccountHeader
    // it replaces did — keeping horizontal alignment and the gap to the Subnav
    // below identical across breakpoints, rather than re-deriving that padding.
    <Header>
      <section
        ref={fullHeroRef}
        css={style.hero}
        data-testid="card-detail-hero"
        aria-hidden={compactOpen}
        // Once the compact bar takes over, the full hero has scrolled under the
        // global header; make it inert so its (now duplicated) controls leave the
        // a11y tree and tab order while it stays mounted for the jank-free swap.
        {...(compactOpen ? { inert: '' } : {})}
      >
        <div css={style.left} data-testid="card-detail-hero-left">
          <CardSummary
            creditCardId={creditCardId}
            status={status}
            canUpdateCard={canUpdateCard}
            // The full hero goes inert once the compact bar takes over, so it
            // hands the live-region role across with it.
            announceStates={!compactOpen}
          />
        </div>
        <div
          css={style.right}
          data-testid="card-detail-hero-right"
          ref={reveal.containerRefFor('full')}
        >
          <div css={style.toolbar} data-testid="card-detail-hero-toolbar">
            <CardDisplayActions
              status={status}
              onFreeze={toggleFreeze}
              revealOpen={reveal.isOpenFor('full')}
              onToggleReveal={() => reveal.toggle('full')}
              revealPanelId={revealPanelId}
            />
            <CardActionsMenu />
          </div>
          {/*
            The art is pure identity, so it waits for the identity group rather
            than rendering a card face full of blanks. CardSummary announces the
            group's loading/error state in text — one announcement, not two.
            `exp` is the card's real expiry now; only PAN and CVC remain demo.
          */}
          {card !== undefined && (
            <CardArt
              status={status}
              network={card.network}
              last4={card.last4}
              holder={card.cardholder.displayName}
              imageUrl={card.cardArt.imageUrl}
              textColor={card.cardArt.textColor}
              textShadow={card.cardArt.textShadow}
              pan={credentials.pan}
              exp={card.expiry}
              revealed={reveal.isOpenFor('full')}
            />
          )}
          {reveal.isOpenFor('full') && card !== undefined && (
            <CardRevealPanel
              id={revealPanelId}
              cardholderName={card.cardholder.displayName}
              address={billing.address}
              pan={credentials.pan}
              cvc={credentials.cvc}
            />
          )}
        </div>
      </section>

      {/*
        The pinned condensed hero. Always mounted (inert until the swap opens it),
        so its ids are unique per variant and its reveal is a distinct anchor. It
        is `position: fixed`, rendered as a sibling of the full hero.
      */}
      <CardHeroCompact
        open={compactOpen}
        fixedElementPushdown={fixedElementPushdown}
        sideNavLockedOpen={sideNavLockedOpen}
        creditCardId={creditCardId}
        status={status}
        onFreeze={toggleFreeze}
        revealOpen={reveal.isOpenFor('compact')}
        onToggleReveal={() => reveal.toggle('compact')}
        revealContainerRef={reveal.containerRefFor('compact')}
      />
    </Header>
  )
}

export default CardDetailHero
